import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const BusinessTransaction = ({ onGetStarted }) => {
  const benefits = [
    'Strategic transaction planning and structuring',
    'Comprehensive due diligence services',
    'Negotiation support and guidance',
    'Document drafting and review',
    'Regulatory compliance assistance',
    'Post-closing integration support',
  ];

  const process = [
    { title: 'Transaction Planning', description: 'Assess objectives and develop strategic transaction approach.' },
    { title: 'Due Diligence', description: 'Conduct thorough review of business, financial, and legal matters.' },
    { title: 'Negotiation & Documentation', description: 'Negotiate terms and prepare comprehensive transaction documents.' },
    { title: 'Closing & Integration', description: 'Execute transaction and support post-closing integration.' },
  ];

  const transactionTypes = [
    'Asset Purchases',
    'Stock Purchases',
    'Joint Ventures',
    'Strategic Partnerships',
    'Equity Investments',
    'Licensing Agreements',
    'Franchise Transactions',
    'Business Reorganizations',
  ];

  const whyChoose = [
    { icon: Award, title: 'Transaction Expertise', description: 'Extensive experience across all types of business transactions.' },
    { icon: Shield, title: 'Risk Management', description: 'Comprehensive due diligence to identify and mitigate risks.' },
    { icon: Clock, title: 'Efficient Execution', description: 'Streamlined process to close transactions on schedule.' },
    { icon: TrendingUp, title: 'Value Maximization', description: 'Strategic guidance to maximize transaction value.' },
  ];

  const faqs = [
    { question: 'What is involved in business transaction legal services?', answer: 'Our transaction services include strategic planning, due diligence, document preparation, negotiation support, regulatory compliance, and post-closing assistance. We guide clients through every phase of the transaction process.' },
    { question: 'How long does a typical business transaction take?', answer: 'Timeline varies significantly based on transaction complexity, size, and type. Simple transactions may close in weeks, while complex deals can take several months. We work efficiently to meet your timeline goals.' },
    { question: 'What is due diligence and why is it important?', answer: 'Due diligence is the comprehensive investigation of a business before a transaction. It examines financial records, legal matters, contracts, intellectual property, and potential liabilities. Proper due diligence protects against unexpected issues and informs negotiation strategy.' },
    { question: 'What documents are needed for a business transaction?', answer: 'Typical documents include purchase agreements, disclosure schedules, employment agreements, non-compete agreements, escrow agreements, and various corporate resolutions. Specific requirements depend on transaction structure and industry.' },
    { question: 'How do you ensure confidentiality during transactions?', answer: 'We implement strict confidentiality protocols including non-disclosure agreements, secure document sharing platforms, and limited information disclosure. Protecting sensitive business information is paramount throughout the transaction process.' },
  ];

  return (
    <div className="pt-20" data-testid="service-business-transaction">
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758599543129-5269a8f29e68?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Business Law Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Business Transaction</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Strategic guidance for business transactions and commercial deals. We handle every aspect from planning through closing.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Business transactions require careful planning, thorough due diligence, and strategic execution. Our attorneys provide comprehensive transaction support from initial planning through post-closing integration.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">We represent buyers, sellers, and investors in all types of business transactions.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1681505531034-8d67054e07f6?w=800&q=80" alt="Business transaction agreement" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Transaction Types</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">We handle all types of business transactions and commercial deals.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {transactionTypes.map((type, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6 hover:border-[#2C3E50]/20 transition-all">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{type}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">A strategic approach to business transactions that ensures successful outcomes.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.pexels.com/photos/5520322/pexels-photo-5520322.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Why choose our transaction team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Ready to Execute Your Transaction?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for strategic business transaction legal services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default BusinessTransaction;
